/**
 * QuickStaffOnboarding - Bulk import staff from CSV
 * Supports both housing and dining staff with auto-generated passwords
 */
import React, { useState, useRef } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Upload, FileText, Download, AlertCircle, CheckCircle2,
  Loader2, Users, Mail, Building2, ChefHat
} from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Switch } from './ui/switch';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { ScrollArea } from './ui/scroll-area';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { toast } from 'sonner';
import { getToken } from '@/lib/authToken';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Role definitions
const HOUSING_ROLES = [
  { id: 'housing_manager', name: 'Housing Manager' },
  { id: 'front_desk', name: 'Front Desk' },
  { id: 'housekeeping', name: 'Housekeeping' },
  { id: 'viewer', name: 'Viewer' }
];

const KITCHEN_ROLES = [
  { id: 'head_chef', name: 'Head Chef' },
  { id: 'line_cook', name: 'Line Cook' },
  { id: 'prep_cook', name: 'Prep Cook' },
  { id: 'kitchen_helper', name: 'Kitchen Helper' }
];

const POS_ROLES = [
  { id: 'pos_manager', name: 'POS Manager' },
  { id: 'cashier', name: 'Cashier' },
  { id: 'order_taker', name: 'Order Taker' },
  { id: 'viewer', name: 'Viewer' }
];

const QuickStaffOnboarding = ({ staffType = 'housing', eventId = null, onSuccess }) => {
  const queryClient = useQueryClient();
  const fileInputRef = useRef(null);
  const [isOpen, setIsOpen] = useState(false);
  const [activeTab, setActiveTab] = useState(staffType);
  const [csvData, setCsvData] = useState([]);
  const [errors, setErrors] = useState([]);
  const [sendCredentials, setSendCredentials] = useState(true);
  const [defaultRole, setDefaultRole] = useState('');
  const [importing, setImporting] = useState(false);
  const [importResults, setImportResults] = useState(null);

  const token = getToken();
  const headers = { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' };

  const getRolesForType = (type) => {
    switch (type) {
      case 'housing': return HOUSING_ROLES;
      case 'kitchen': return KITCHEN_ROLES;
      case 'pos': return POS_ROLES;
      default: return [];
    }
  };

  const parseCSV = (text) => {
    const lines = text.trim().split('\n');
    if (lines.length < 2) {
      setErrors(['CSV must have a header row and at least one data row']);
      return [];
    }

    const headerLine = lines[0].toLowerCase();
    const csvHeaders = headerLine.split(',').map(h => h.trim().replace(/"/g, ''));
    
    // Required columns
    const requiredCols = ['name', 'email'];
    const missingCols = requiredCols.filter(col => !csvHeaders.includes(col));
    
    if (missingCols.length > 0) {
      setErrors([`Missing required columns: ${missingCols.join(', ')}`]);
      return [];
    }

    const data = [];
    const parseErrors = [];

    for (let i = 1; i < lines.length; i++) {
      const line = lines[i].trim();
      if (!line) continue;

      // Handle quoted values with commas
      const values = [];
      let current = '';
      let inQuotes = false;
      
      for (const char of line) {
        if (char === '"') {
          inQuotes = !inQuotes;
        } else if (char === ',' && !inQuotes) {
          values.push(current.trim());
          current = '';
        } else {
          current += char;
        }
      }
      values.push(current.trim());

      if (values.length !== csvHeaders.length) {
        parseErrors.push(`Row ${i + 1}: Column count mismatch`);
        continue;
      }

      const row = {};
      csvHeaders.forEach((header, idx) => {
        row[header] = values[idx]?.replace(/"/g, '') || '';
      });

      // Validate email
      if (!row.email || !row.email.includes('@')) {
        parseErrors.push(`Row ${i + 1}: Invalid email "${row.email}"`);
        continue;
      }

      // Validate required fields
      if (!row.name) {
        parseErrors.push(`Row ${i + 1}: Missing name`);
        continue;
      }

      // Validate role if provided
      const roles = getRolesForType(activeTab);
      if (row.role && !roles.find(r => r.id === row.role)) {
        parseErrors.push(`Row ${i + 1}: Invalid role "${row.role}". Valid: ${roles.map(r => r.id).join(', ')}`);
        continue;
      }

      data.push({
        name: row.name,
        email: row.email,
        phone: row.phone || '',
        role: row.role || defaultRole || roles[1]?.id || roles[0]?.id,
        is_temporary: row.is_temporary === 'true' || row.is_temporary === '1',
        event_id: row.event_id || eventId || ''
      });
    }

    setErrors(parseErrors);
    return data;
  };

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.name.endsWith('.csv')) {
      toast.error('Please select a CSV file');
      return;
    }

    const reader = new FileReader();
    reader.onload = (event) => {
      const text = event.target?.result;
      if (typeof text === 'string') {
        const data = parseCSV(text);
        setCsvData(data);
      }
    };
    reader.readAsText(file);
  };

  const handleImport = async () => {
    if (csvData.length === 0) {
      toast.error('No valid data to import');
      return;
    }

    setImporting(true);
    setImportResults(null);

    try {
      const endpoint = activeTab === 'housing' 
        ? `${API_URL}/api/admin/accommodation/staff/bulk-import`
        : `${API_URL}/api/admin/dining/staff/bulk-import`;

      const res = await fetch(endpoint, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          staff: csvData.map(s => ({
            ...s,
            staff_type: activeTab === 'housing' ? undefined : activeTab
          })),
          send_credentials: sendCredentials,
          default_role: defaultRole || undefined
        })
      });

      const data = await res.json();
      
      if (!res.ok) {
        throw new Error(data.detail || 'Import failed');
      }

      setImportResults(data);
      
      // Invalidate relevant queries
      if (activeTab === 'housing') {
        queryClient.invalidateQueries(['housing-staff']);
      } else {
        queryClient.invalidateQueries(['dining-staff']);
      }
      
      toast.success(`Successfully imported ${data.imported} staff members`);
      
      if (onSuccess) {
        onSuccess(data);
      }
    } catch (error) {
      toast.error(error.message);
    } finally {
      setImporting(false);
    }
  };

  const downloadTemplate = () => {
    const roles = getRolesForType(activeTab);
    const roleExamples = roles.slice(0, 2).map(r => r.id).join(' or ');
    const template = `name,email,phone,role,is_temporary
John Doe,john@example.com,+1234567890,${roles[1]?.id || roles[0]?.id},false
Jane Smith,jane@example.com,+0987654321,${roles[0]?.id},false
Temp Worker,temp@example.com,,${roles[roles.length - 1]?.id},true`;
    
    const blob = new Blob([template], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${activeTab}_staff_import_template.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const resetState = () => {
    setCsvData([]);
    setErrors([]);
    setImportResults(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const getIcon = () => {
    switch (activeTab) {
      case 'housing': return <Building2 className="h-5 w-5 text-indigo-600" />;
      case 'kitchen': return <ChefHat className="h-5 w-5 text-orange-600" />;
      case 'pos': return <Users className="h-5 w-5 text-blue-600" />;
      default: return <Users className="h-5 w-5" />;
    }
  };

  return (
    <>
      <Button onClick={() => setIsOpen(true)} variant="outline" data-testid="quick-staff-onboarding-btn">
        <Upload className="h-4 w-4 mr-2" />
        Quick Onboarding
      </Button>

      <Dialog open={isOpen} onOpenChange={(open) => { setIsOpen(open); if (!open) resetState(); }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {getIcon()}
              Quick Staff Onboarding
            </DialogTitle>
          </DialogHeader>

          <Tabs value={activeTab} onValueChange={(v) => { setActiveTab(v); resetState(); }}>
            <TabsList className="grid grid-cols-2 sm:grid-cols-3 w-full">
              <TabsTrigger value="housing" className="gap-2">
                <Building2 className="h-4 w-4" />
                Housing
              </TabsTrigger>
              <TabsTrigger value="kitchen" className="gap-2">
                <ChefHat className="h-4 w-4" />
                Kitchen
              </TabsTrigger>
              <TabsTrigger value="pos" className="gap-2">
                <Users className="h-4 w-4" />
                POS
              </TabsTrigger>
            </TabsList>

            <div className="mt-4 space-y-4">
              {/* Instructions */}
              <Card className="bg-blue-50 border-blue-200">
                <CardContent className="pt-4">
                  <div className="flex items-start gap-3">
                    <FileText className="h-5 w-5 text-blue-600 mt-0.5" />
                    <div className="text-sm">
                      <p className="font-medium text-blue-900">CSV Format:</p>
                      <ul className="mt-1 text-blue-700 space-y-1">
                        <li>• Required: <code className="bg-blue-100 px-1 rounded">name</code>, <code className="bg-blue-100 px-1 rounded">email</code></li>
                        <li>• Optional: phone, role, is_temporary</li>
                        <li>• Roles: {getRolesForType(activeTab).map(r => r.id).join(', ')}</li>
                        <li>• Passwords will be auto-generated</li>
                      </ul>
                      <Button 
                        variant="link" 
                        size="sm" 
                        className="p-0 h-auto text-blue-600 mt-2"
                        onClick={downloadTemplate}
                      >
                        <Download className="h-3 w-3 mr-1" />
                        Download {activeTab} staff template
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* File Upload */}
              <div>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".csv"
                  onChange={handleFileSelect}
                  className="hidden"
                  id="staff-csv-upload"
                />
                <label
                  htmlFor="staff-csv-upload"
                  className="flex items-center justify-center border-2 border-dashed rounded-lg p-8 cursor-pointer hover:border-blue-400 hover:bg-blue-50/50 transition-colors"
                >
                  <div className="text-center">
                    <Upload className="h-10 w-10 text-gray-400 mx-auto mb-2" />
                    <p className="text-sm text-gray-600">Click to upload CSV file</p>
                    <p className="text-xs text-gray-400 mt-1">or drag and drop</p>
                  </div>
                </label>
              </div>

              {/* Options */}
              <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                <div className="flex items-center gap-2">
                  <Switch
                    id="send-creds"
                    checked={sendCredentials}
                    onCheckedChange={setSendCredentials}
                  />
                  <Label htmlFor="send-creds" className="flex items-center gap-1">
                    <Mail className="h-4 w-4" />
                    Email login credentials to staff
                  </Label>
                </div>
                <div className="flex items-center gap-2">
                  <Label>Default Role:</Label>
                  <Select value={defaultRole} onValueChange={setDefaultRole}>
                    <SelectTrigger className="w-40">
                      <SelectValue placeholder="Select role" />
                    </SelectTrigger>
                    <SelectContent>
                      {getRolesForType(activeTab).map(role => (
                        <SelectItem key={role.id} value={role.id}>{role.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Errors */}
              {errors.length > 0 && (
                <Card className="border-red-200 bg-red-50">
                  <CardContent className="pt-4">
                    <div className="flex items-start gap-2">
                      <AlertCircle className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="font-medium text-red-900">Validation Errors:</p>
                        <ul className="mt-1 text-sm text-red-700 space-y-1">
                          {errors.slice(0, 5).map((err, i) => (
                            <li key={i}>• {err}</li>
                          ))}
                          {errors.length > 5 && (
                            <li className="text-red-500">...and {errors.length - 5} more errors</li>
                          )}
                        </ul>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Preview */}
              {csvData.length > 0 && (
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm flex items-center gap-2">
                      <CheckCircle2 className="h-4 w-4 text-green-600" />
                      Ready to import {csvData.length} staff members
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <ScrollArea className="h-48">
                      <table className="w-full text-sm">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="text-left p-2">Name</th>
                            <th className="text-left p-2">Email</th>
                            <th className="text-left p-2">Role</th>
                            <th className="text-left p-2">Type</th>
                          </tr>
                        </thead>
                        <tbody>
                          {csvData.slice(0, 10).map((row, i) => (
                            <tr key={i} className="border-t">
                              <td className="p-2">{row.name}</td>
                              <td className="p-2">{row.email}</td>
                              <td className="p-2">{getRolesForType(activeTab).find(r => r.id === row.role)?.name || row.role}</td>
                              <td className="p-2">
                                {row.is_temporary ? (
                                  <span className="text-orange-600">Temporary</span>
                                ) : (
                                  <span className="text-green-600">Permanent</span>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                      {csvData.length > 10 && (
                        <p className="text-center text-gray-500 py-2">
                          ...and {csvData.length - 10} more
                        </p>
                      )}
                    </ScrollArea>
                  </CardContent>
                </Card>
              )}

              {/* Import Results */}
              {importResults && (
                <Card className="border-green-200 bg-green-50">
                  <CardContent className="pt-4">
                    <div className="flex items-start gap-2">
                      <CheckCircle2 className="h-5 w-5 text-green-600" />
                      <div>
                        <p className="font-medium text-green-900">Import Complete!</p>
                        <ul className="mt-1 text-sm text-green-700">
                          <li>✓ {importResults.imported} staff members imported</li>
                          {importResults.skipped > 0 && (
                            <li>⚠ {importResults.skipped} skipped (duplicate emails)</li>
                          )}
                          {importResults.emails_sent > 0 && (
                            <li>✉ {importResults.emails_sent} credential emails sent</li>
                          )}
                        </ul>
                        {importResults.generated_passwords && (
                          <div className="mt-2 p-2 bg-white rounded border">
                            <p className="text-xs font-medium text-gray-700 mb-1">Generated Passwords (save these!):</p>
                            <ul className="text-xs text-gray-600 space-y-0.5 font-mono">
                              {Object.entries(importResults.generated_passwords).slice(0, 5).map(([email, pwd]) => (
                                <li key={email}>{email}: {pwd}</li>
                              ))}
                              {Object.keys(importResults.generated_passwords).length > 5 && (
                                <li>...and more</li>
                              )}
                            </ul>
                          </div>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>
          </Tabs>

          <DialogFooter>
            <Button variant="outline" onClick={() => { setIsOpen(false); resetState(); }}>
              {importResults ? 'Close' : 'Cancel'}
            </Button>
            {!importResults && (
              <Button
                onClick={handleImport}
                disabled={csvData.length === 0 || importing}
                data-testid="confirm-staff-import-btn"
              >
                {importing ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Importing...
                  </>
                ) : (
                  <>
                    <Upload className="h-4 w-4 mr-2" />
                    Import {csvData.length} Staff
                  </>
                )}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default QuickStaffOnboarding;
